import { useEffect, useRef } from 'react';
import { Link, useParams } from 'react-router';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useQuery } from '@tanstack/react-query';

import { cn } from '#/lib/utils';
import { createStockTransfer } from '#/api/imsMgmt/receipts/stockTransfer/create';
import { fetchStockTransferDetail } from '#/api/imsMgmt/receipts/stockTransfer/fetchDetail';
import { updateStockTransfer } from '#/api/imsMgmt/receipts/stockTransfer/update';
import { fetchInventoryStockLookup } from '#/api/imsMgmt/inventoryStock/fetchLookup';
import { DocumentStatusBadge } from '#/modules/commonComponents/badge/DocumentStatusBadge';
import { Button } from '#/modules/commonComponents/button';
import FloatingDateInput from '#/modules/commonComponents/form/FloatingDateInput';
import FloatingSelect from '#/modules/commonComponents/form/select';
import FloatingTextarea from '#/modules/commonComponents/form/FloatingTextarea';
import FormValidationSummary from '#/modules/commonComponents/form/FormValidationSummary';
import PageShell from '#/modules/infra/layout/PageShell';
import useCreateEditSave from '#/hooks/useCreateEditSave';
import useFormValidationSummary from '#/hooks/useFormValidationSummary';
import StockTransferLinesEditor from '#/modules/imsMgmt/receipts/stockTransfer/components/StockTransferLinesEditor';
import {
  getStockTransferApiErrorMessage,
  isStockTransferVersionConflict,
} from '#/modules/imsMgmt/receipts/stockTransfer/helper/apiError';
import {
  applyLiveInventoryAvailableQty,
  buildStockTransferWritePayload,
  mapStockTransferDetailToFormValues,
} from '#/modules/imsMgmt/receipts/stockTransfer/helper/formSerialize';
import { useStockTransferEntryRoutes } from '#/modules/imsMgmt/receipts/stockTransfer/helper/stockTransferEntryRoutes';
import { STOCK_TRANSFER_DATE_TIP } from '#/modules/imsMgmt/receipts/stockTransfer/helper/transferDateTip';
import { canEditStockTransfer } from '#/modules/imsMgmt/receipts/stockTransfer/helper/transferStatus';
import {
  STOCK_TRANSFER_FIELD_LABELS,
  stockTransferFormSchema,
} from '#/modules/imsMgmt/receipts/stockTransfer/schemas/stockTransferSchema';
import { fetchWarehouseSelectOptions } from '#/modules/imsMgmt/settings/storePlace/helper/warehouseSelectOptions';
import {
  lookupQueryKey,
  stockTransferDetailQueryKey,
  stockTransferListQueryKeyPrefix,
} from '#/utils/queryKeys';
import { toast } from '#/utils/toast';

function missingStockLabel(lines, stockId) {
  const line = lines.find((item) => item.invStockId === stockId);
  return line?.stockNo || stockId;
}

async function loadLiveAvailableByStockId(lines) {
  const stockIds = [...new Set(
    lines
      .map((line) => String(line.invStockId ?? '').trim())
      .filter(Boolean),
  )];
  if (stockIds.length === 0) {
    return { inventoryById: {}, missingLabels: [] };
  }

  try {
    const result = await fetchInventoryStockLookup(stockIds);
    const inventoryById = {};
    (result.data ?? []).forEach((stock) => {
      const stockId = String(stock?.id ?? '').trim();
      if (stockId) {
        inventoryById[stockId] = stock;
      }
    });
    const missingLabels = stockIds
      .filter((stockId) => !inventoryById[stockId])
      .map((stockId) => missingStockLabel(lines, stockId));
    return { inventoryById, missingLabels };
  } catch {
    return {
      inventoryById: {},
      missingLabels: stockIds.map((stockId) => missingStockLabel(lines, stockId)),
    };
  }
}

const DEFAULT_VALUES = {
  transferDate: '',
  toWarehouseId: '',
  sourceType: 'MANUAL',
  sourceId: '',
  issueReason: '',
  remark: '',
  lines: [],
};

const CreateEditStockTransferPage = () => {
  const { id } = useParams();
  const transferRoutes = useStockTransferEntryRoutes();
  const updatedAtRef = useRef(null);
  const missingToastKeyRef = useRef('');
  const {
    isSubmitAttempted,
    markSubmitAttempted,
    resetValidationSummary,
    customMessages,
  } = useFormValidationSummary();

  const detailQuery = useQuery({
    queryKey: stockTransferDetailQueryKey(id),
    queryFn: () => fetchStockTransferDetail(id),
    enabled: Boolean(id),
  });

  const warehouseOptionsQuery = useQuery({
    queryKey: lookupQueryKey('warehouseSelect'),
    queryFn: fetchWarehouseSelectOptions,
  });

  const detail = detailQuery.data?.data;
  const shouldLoadLiveAvailable = Boolean(
    id && detail && canEditStockTransfer(detail.status),
  );
  const liveAvailableQuery = useQuery({
    queryKey: [
      'erp',
      'inventoryStock',
      'transferAvailable',
      id ?? null,
      detail?.updatedAt ?? null,
    ],
    enabled: shouldLoadLiveAvailable,
    staleTime: Infinity,
    refetchOnWindowFocus: false,
    queryFn: () => loadLiveAvailableByStockId(
      mapStockTransferDetailToFormValues(detail).lines ?? [],
    ),
  });

  const { isEdit, isPending, save } = useCreateEditSave({
    id,
    createFn: createStockTransfer,
    updateFn: updateStockTransfer,
    buildPayload: (values) => buildStockTransferWritePayload(values, {
      updatedAt: id ? updatedAtRef.current : null,
    }),
    listQueryKeyPrefix: stockTransferListQueryKeyPrefix,
    detailQueryKey: stockTransferDetailQueryKey,
    getEditPath: transferRoutes.edit,
    getErrorMessage: (error) => getStockTransferApiErrorMessage(error, '儲存失敗'),
    isVersionConflict: isStockTransferVersionConflict,
    onVersionConflict: () => detailQuery.refetch(),
  });

  const {
    register,
    control,
    handleSubmit,
    reset,
    setValue,
    getValues,
    formState: { errors },
  } = useForm({
    resolver: zodResolver(stockTransferFormSchema),
    defaultValues: DEFAULT_VALUES,
  });

  useEffect(() => {
    if (!detail) {
      return;
    }

    updatedAtRef.current = detail.updatedAt ?? detail.record?.updatedAt ?? null;
    if (!canEditStockTransfer(detail.status)) {
      resetValidationSummary();
      reset(mapStockTransferDetailToFormValues(detail));
      return;
    }
    if (!liveAvailableQuery.data) {
      return;
    }

    const mapped = mapStockTransferDetailToFormValues(detail);
    const { inventoryById, missingLabels } = liveAvailableQuery.data;
    const toastKey = `${detail.updatedAt ?? ''}:${missingLabels.join('、')}`;
    if (missingLabels.length > 0 && missingToastKeyRef.current !== toastKey) {
      missingToastKeyRef.current = toastKey;
      toast.error(`讀不到庫存可用量：${missingLabels.join('、')}`);
    }
    resetValidationSummary();
    reset(applyLiveInventoryAvailableQty(mapped, inventoryById));
  }, [detail, liveAvailableQuery.data, reset, resetValidationSummary]);

  function handleInvalidSubmit() {
    markSubmitAttempted();
  }

  function onSubmit(values) {
    markSubmitAttempted();
    save(values);
  }

  const pageTitle = isEdit ? '編輯轉倉單' : '新增轉倉單';
  const headerStatus = isEdit ? (detail?.status || 'DRAFT') : 'DRAFT';
  const isWaitingForAvailable = shouldLoadLiveAvailable && liveAvailableQuery.isLoading;
  const isPageLoading = isEdit && (detailQuery.isLoading || isWaitingForAvailable);
  const isDetailNotFound = isEdit && detailQuery.isError;
  const isReadOnlyEdit = isEdit && detail && !canEditStockTransfer(detail.status);
  const warehouseOptions = warehouseOptionsQuery.data ?? [];

  return (
    <PageShell variant="form">
      <div className="layout-shell__body">
        <div className="w-full">
          <div className="create_edit_page__header">
            <h1 className="page_title mb-0! flex flex-wrap items-center gap-2">
              <span className="border-l-5 border-primary-400 pl-2.5">{pageTitle}</span>
              <DocumentStatusBadge status={headerStatus} size="sm" />
            </h1>
            <Button type="button" variant="outline" asChild>
              <Link to={id ? transferRoutes.detail(id) : transferRoutes.list}>
                {id ? '返回詳情' : '返回列表'}
              </Link>
            </Button>
          </div>

          {isPageLoading ? (
            <p className="text-sm text-slate-500">載入中…</p>
          ) : null}

          {isDetailNotFound ? (
            <p className="text-sm text-destructive">
              {getStockTransferApiErrorMessage(detailQuery.error, '找不到轉倉單')}
            </p>
          ) : null}

          {isReadOnlyEdit ? (
            <div className="space-y-3">
              <p className="flex flex-wrap items-center gap-2 text-sm text-slate-600">
                <span>僅草稿可編輯。目前狀態為</span>
                <DocumentStatusBadge status={detail.status} size="sm" />
                <span>，請至詳情頁操作。</span>
              </p>
              <Button type="button" asChild>
                <Link to={transferRoutes.detail(id)}>前往詳情</Link>
              </Button>
            </div>
          ) : null}

          {!isPageLoading && !isDetailNotFound && !isReadOnlyEdit ? (
            <div className="space-y-4">
              <FormValidationSummary
                show={isSubmitAttempted}
                errors={errors}
                fieldLabels={STOCK_TRANSFER_FIELD_LABELS}
                customMessages={customMessages}
              />

              <form
                className="sheet create_edit create_edit_form create_edit_form--page-scroll"
                onSubmit={handleSubmit(onSubmit, handleInvalidSubmit)}
                noValidate
              >
                <div className="create_edit_form__body divide-y divide-gray-500">
                  <section className="pb-6">
                    <div className="form-block">
                      <h2 className="form-block__title">單據資訊</h2>
                    </div>
                    <div
                      className={cn(
                        'grid grid-cols-1 gap-3',
                        '@4xl/sheet:grid-cols-2',
                      )}
                    >
                      <FloatingDateInput
                        id="transferDate"
                        label="轉倉日"
                        tip={STOCK_TRANSFER_DATE_TIP}
                        control={control}
                        errors={errors}
                        placeholder="西元年/月/日"
                      />
                      <FloatingSelect
                        id="toWarehouseId"
                        label="轉倉倉別"
                        options={warehouseOptions}
                        control={control}
                        errors={errors}
                        isDisabled={warehouseOptionsQuery.isLoading}
                        noOptionsMessage="無可用倉別"
                      />
                      <FloatingTextarea
                        id="issueReason"
                        label="事由"
                        register={register}
                        errors={errors}
                      />
                      <FloatingTextarea
                        id="remark"
                        label="備註"
                        register={register}
                        errors={errors}
                      />
                    </div>
                  </section>

                  <StockTransferLinesEditor
                    control={control}
                    register={register}
                    errors={errors}
                    setValue={setValue}
                    getValues={getValues}
                  />
                </div>

                <div className="create_edit_form__actions mt-4 flex flex-wrap justify-end gap-2">
                  <Button type="button" variant="outline" asChild>
                    <Link to={id ? transferRoutes.detail(id) : transferRoutes.list}>
                      取消
                    </Link>
                  </Button>
                  <Button type="submit" disabled={isPending}>
                    {isPending ? '儲存中…' : '儲存'}
                  </Button>
                </div>
              </form>
            </div>
          ) : null}
        </div>
      </div>
    </PageShell>
  );
};

export default CreateEditStockTransferPage;
